import type { RiskFlowAlert } from "../../lib/riskflow-feed";

export interface TiltScoutHeadlineWeight {
  headlineWeight: number;
  headlineIvScore: number | null;
  headlineEventWeight: number | null;
  headlineFreshness: number;
  headlineImpactWeight: number;
  headlineWeightSource: "ivScore" | "eventWeight" | "macroLevel" | "fallback";
}

const IMPACT_HALF_LIFE_HOURS = 6;
const IMPACT_FLOOR = 0.25;

export function headlineWeightForAlert(
  alert: Pick<
    RiskFlowAlert,
    | "headlineWeight"
    | "headlineIvScore"
    | "headlineEventWeight"
    | "headlineWeightSource"
    | "headlineFreshness"
    | "headlineImpactWeight"
    | "ivScore"
    | "subScores"
    | "severity"
    | "publishedAt"
  >,
): TiltScoutHeadlineWeight {
  const explicit = normalizeTenPoint(alert.headlineWeight);
  const headlineIvScore = normalizeTenPoint(
    alert.headlineIvScore ?? alert.ivScore,
  );
  const headlineEventWeight = normalizeTenPoint(
    alert.headlineEventWeight ?? alert.subScores?.eventWeight,
  );

  if (explicit != null) {
    return withImpact(
      {
        headlineWeight: roundWeight(Math.max(1, explicit)),
        headlineIvScore,
        headlineEventWeight,
        headlineWeightSource: alert.headlineWeightSource ?? "ivScore",
      },
      alert,
    );
  }

  if (headlineIvScore != null && headlineEventWeight != null) {
    const blendedWeight = headlineIvScore * 0.75 + headlineEventWeight * 0.25;
    const eventFloor =
      headlineEventWeight >= 6 ? headlineEventWeight * 0.85 : 0;
    const headlineWeight = roundWeight(Math.max(1, blendedWeight, eventFloor));

    return withImpact(
      {
        headlineWeight,
        headlineIvScore,
        headlineEventWeight,
        headlineWeightSource:
          eventFloor > blendedWeight ? "eventWeight" : "ivScore",
      },
      alert,
    );
  }

  if (headlineIvScore != null) {
    return withImpact(
      {
        headlineWeight: roundWeight(Math.max(1, headlineIvScore)),
        headlineIvScore,
        headlineEventWeight,
        headlineWeightSource: "ivScore",
      },
      alert,
    );
  }

  if (headlineEventWeight != null) {
    return withImpact(
      {
        headlineWeight: roundWeight(Math.max(1, headlineEventWeight)),
        headlineIvScore,
        headlineEventWeight,
        headlineWeightSource: "eventWeight",
      },
      alert,
    );
  }

  return withImpact(
    {
      headlineWeight: severityWeight(alert.severity),
      headlineIvScore,
      headlineEventWeight,
      headlineWeightSource: alert.severity ? "macroLevel" : "fallback",
    },
    alert,
  );
}

function withImpact(
  weight: Omit<
    TiltScoutHeadlineWeight,
    "headlineFreshness" | "headlineImpactWeight"
  >,
  alert: Pick<
    RiskFlowAlert,
    "headlineFreshness" | "headlineImpactWeight" | "publishedAt"
  >,
): TiltScoutHeadlineWeight {
  const explicitImpact = normalizeTenPoint(alert.headlineImpactWeight);
  const explicitFreshness = normalizeFreshness(alert.headlineFreshness);
  const headlineFreshness =
    explicitFreshness ?? freshnessMultiplier(alert.publishedAt);

  return {
    ...weight,
    headlineFreshness,
    headlineImpactWeight:
      explicitImpact ?? roundWeight(weight.headlineWeight * headlineFreshness),
  };
}

function severityWeight(severity?: RiskFlowAlert["severity"]): number {
  if (severity === "critical") return 7.2;
  if (severity === "high") return 5.4;
  if (severity === "medium") return 3.6;
  if (severity === "low") return 1.8;
  return 3;
}

function normalizeTenPoint(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const normalized = value > 10 ? value / 10 : value;
  return Math.max(0, Math.min(10, normalized));
}

function normalizeFreshness(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(0, Math.min(1, value));
}

function freshnessMultiplier(publishedAt: string | undefined): number {
  const publishedMs = Date.parse(publishedAt ?? "");
  if (!Number.isFinite(publishedMs)) return IMPACT_FLOOR;

  const ageHours = Math.max(0, (Date.now() - publishedMs) / 3_600_000);
  const decayed = Math.pow(0.5, ageHours / IMPACT_HALF_LIFE_HOURS);
  return Math.round(Math.max(IMPACT_FLOOR, Math.min(1, decayed)) * 100) / 100;
}

function roundWeight(value: number): number {
  return Math.round(Math.max(0, Math.min(10, value)) * 10) / 10;
}
